import React, { useEffect, useState } from 'react';
import { leaveApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function LeavePage() {
  const { isManager, isHR, isAdmin } = useAuth();
  const [tab, setTab] = useState('my');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [historyLeaves, setHistoryLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leaveTypeId: '', startDate: '', endDate: '', totalDays: 1, isHalfDay: false, halfDayPeriod: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionId, setActionId] = useState(null);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const exportLeaveReport = () => {
    let rows = historyLeaves.filter(lr => lr.status === 'HR_APPROVED' || lr.status === 'MANAGER_APPROVED');
    if (exportFrom) rows = rows.filter(lr => lr.startDate >= exportFrom);
    if (exportTo) rows = rows.filter(lr => lr.endDate <= exportTo);
    if (rows.length === 0) { alert('No approved leaves found for the selected date range'); return; }
    const header = ['Employee', 'Employee ID', 'Team', 'Leave Type', 'Start Date', 'End Date', 'Days', 'Reason', 'Status', 'Manager', 'Manager Approved', 'HR', 'HR Approved'];
    const csvRows = rows.map(lr => [
      lr.employeeName, lr.employeeEmpId, lr.teamName || '', lr.leaveTypeName,
      lr.startDate, lr.endDate, lr.totalDays, `"${(lr.reason || '').replace(/"/g, '""')}"`,
      lr.status.replace(/_/g, ' '), lr.managerName || '', lr.managerActedAt || '',
      lr.hrName || '', lr.hrActedAt || ''
    ]);
    const csv = [header.join(','), ...csvRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-report-${exportFrom || 'all'}-to-${exportTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadData();
  }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [typesRes, balRes, myRes] = await Promise.all([
        leaveApi.getTypes(),
        leaveApi.getBalances(),
        leaveApi.getMyLeaves(),
      ]);
      if (typesRes.data.success) setLeaveTypes(typesRes.data.data || []);
      if (balRes.data.success) setBalances(balRes.data.data || []);
      if (myRes.data.success) setMyLeaves(myRes.data.data || []);

      if (tab === 'pending' && (isManager || isHR || isAdmin)) {
        let pendRes, histRes;
        if (isAdmin) {
          pendRes = await leaveApi.getAllPending();
          histRes = await leaveApi.getAllHistory();
        } else if (isHR) {
          pendRes = await leaveApi.getPendingHR();
          histRes = await leaveApi.getHRHistory();
        } else {
          pendRes = await leaveApi.getPendingManager();
          histRes = await leaveApi.getManagerHistory();
        }
        if (pendRes.data.success) setPendingLeaves(pendRes.data.data || []);
        if (histRes.data.success) setHistoryLeaves(histRes.data.data || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await leaveApi.submit(form);
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message || 'Leave request submitted!' });
        setShowForm(false);
        setForm({ leaveTypeId: '', startDate: '', endDate: '', totalDays: 1, isHalfDay: false, halfDayPeriod: '', reason: '' });
        loadData();
      } else {
        setMessage({ type: 'error', text: res.data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error submitting request' });
    }
    setSubmitting(false);
  };

  const handleAction = async (id, action) => {
    try {
      let res;
      if (action === 'manager-approve') res = await leaveApi.managerApprove(id, actionComment);
      else if (action === 'manager-reject') {
        if (!actionComment.trim()) { alert('Please provide a reason for rejection'); return; }
        res = await leaveApi.managerReject(id, actionComment);
      }
      else if (action === 'hr-approve') res = await leaveApi.hrApprove(id, actionComment);
      else if (action === 'hr-reject') {
        if (!actionComment.trim()) { alert('Please provide a reason for rejection'); return; }
        res = await leaveApi.hrReject(id, actionComment);
      }
      setActionComment('');
      setActionId(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    }
  };

  const cancelLeave = async (id) => {
    if (window.confirm('Cancel this leave request?')) {
      try {
        await leaveApi.cancel(id);
        loadData();
      } catch (err) { alert(err.response?.data?.message || 'Cancel failed'); }
    }
  };

  const calcDays = (start, end, isHalf) => {
    if (!start || !end) return 0;
    const s = new Date(start), e = new Date(end);
    if (e < s) return 0;
    let days = 0;
    let cur = new Date(s);
    while (cur <= e) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) days++;
      cur.setDate(cur.getDate() + 1);
    }
    return isHalf ? 0.5 : days;
  };

  const updateDates = (field, value) => {
    const newForm = { ...form, [field]: value };
    newForm.totalDays = calcDays(newForm.startDate, newForm.endDate, newForm.isHalfDay);
    setForm(newForm);
  };

  const statusBadge = (s) => {
    const map = { DRAFT: 'badge-gray', SUBMITTED: 'badge-warning', MANAGER_APPROVED: 'badge-info', HR_APPROVED: 'badge-success', MANAGER_REJECTED: 'badge-danger', HR_REJECTED: 'badge-danger', CANCELLED: 'badge-gray' };
    return map[s] || 'badge-gray';
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Leave Management</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${tab === 'my' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('my')}>My Leaves</button>
          {(isManager || isHR || isAdmin) && (
            <button className={`btn ${tab === 'pending' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('pending')}>
              Pending Approvals
            </button>
          )}
          <button className="btn btn-success" onClick={() => setShowForm(true)}>+ Request Leave</button>
        </div>
      </div>

      {message && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#16a34a' : '#dc2626' }}>
          {message.text}
        </div>
      )}

      {/* Balances */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {balances.map(b => (
          <div className="stat-card" key={b.leaveTypeCode}>
            <div className="stat-label">{b.leaveTypeName}</div>
            <div className="stat-value">{b.available}</div>
            <div className="stat-sub">
              Accrued: {b.accrued} | Used: {b.used} | Pending: {b.pending}
            </div>
          </div>
        ))}
      </div>

      {/* Request Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h3>New Leave Request</h3>
            <button className="btn btn-sm btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Leave Type</label>
                  <select className="form-control" value={form.leaveTypeId} onChange={e => setForm({ ...form, leaveTypeId: e.target.value })} required>
                    <option value="">Select type</option>
                    {leaveTypes.map(lt => (
                      <option key={lt.id} value={lt.id}>{lt.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input className="form-control" type="date" value={form.startDate} onChange={e => updateDates('startDate', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input className="form-control" type="date" value={form.endDate} onChange={e => updateDates('endDate', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={form.isHalfDay} onChange={e => {
                      const isHalf = e.target.checked;
                      setForm(prev => ({ ...prev, isHalfDay: isHalf, endDate: isHalf ? prev.startDate : prev.endDate, totalDays: calcDays(prev.startDate, isHalf ? prev.startDate : prev.endDate, isHalf) }));
                    }} />
                    Half Day
                  </label>
                  {form.isHalfDay && (
                    <select className="form-control" style={{ marginTop: 4 }} value={form.halfDayPeriod} onChange={e => setForm({ ...form, halfDayPeriod: e.target.value })}>
                      <option value="">Select</option>
                      <option value="MORNING">Morning</option>
                      <option value="AFTERNOON">Afternoon</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label>Total Days</label>
                  <input className="form-control" type="number" step="0.5" value={form.totalDays} onChange={e => setForm({ ...form, totalDays: parseFloat(e.target.value) || 0 })} readOnly style={{ background: '#f3f4f6', fontWeight: 700, fontSize: 18 }} />
                </div>
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea className="form-control" rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Provide reason for leave request" required />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* My Leaves Tab */}
      {tab === 'my' && (
        <div className="card">
          <div className="card-header"><h3>My Leave Requests</h3></div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {myLeaves.length === 0 ? (
                  <tr><td colSpan="7" className="empty-state">No leave requests</td></tr>
                ) : myLeaves.map(lr => (
                  <tr key={lr.id}>
                    <td><strong>{lr.leaveTypeName}</strong></td>
                    <td>{lr.startDate} to {lr.endDate}</td>
                    <td>{lr.totalDays}{lr.isHalfDay ? ' (Half)' : ''}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lr.reason}</td>
                    <td><span className={`badge ${statusBadge(lr.status)}`}>{lr.status.replace(/_/g, ' ')}</span></td>
                    <td>{lr.submittedAt || '—'}</td>
                    <td>
                      {['DRAFT', 'SUBMITTED', 'MANAGER_APPROVED'].includes(lr.status) && (
                        <button className="btn btn-sm btn-danger" onClick={() => cancelLeave(lr.id)}>Cancel</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Approvals Tab */}
      {tab === 'pending' && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Pending Requests</h3>
          {pendingLeaves.length === 0 ? (
            <div className="card"><div className="card-body empty-state">No pending leave requests</div></div>
          ) : pendingLeaves.map(lr => (
            <div className="card" key={lr.id} style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div>
                  <h3>{lr.employeeName}</h3>
                  <small style={{ color: '#6b7280' }}>{lr.employeeEmpId} • {lr.teamName || 'No Team'}</small>
                </div>
                <span className={`badge ${statusBadge(lr.status)}`}>{lr.status.replace(/_/g, ' ')}</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div><div className="stat-label">Leave Type</div><strong>{lr.leaveTypeName}</strong></div>
                  <div><div className="stat-label">Dates</div><strong>{lr.startDate} to {lr.endDate}</strong></div>
                  <div><div className="stat-label">Total Days</div><strong>{lr.totalDays}{lr.isHalfDay ? ' (Half)' : ''}</strong></div>
                  <div><div className="stat-label">Submitted</div><strong>{lr.submittedAt}</strong></div>
                </div>
                {lr.reason && <p style={{ marginBottom: 12, color: '#374151' }}><strong>Reason:</strong> {lr.reason}</p>}
                {lr.managerName && <p style={{ fontSize: 12, color: '#6b7280' }}>Manager: {lr.managerName} — {lr.managerComments || 'No comments'}</p>}

                {actionId === lr.id ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="form-group">
                      <label>Comments</label>
                      <textarea className="form-control" rows={2} value={actionComment} onChange={e => setActionComment(e.target.value)} placeholder="Add comments (required for rejection)" />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-outline" onClick={() => { setActionId(null); setActionComment(''); }}>Cancel</button>
                      {lr.status === 'SUBMITTED' && isManager && (
                        <>
                          <button className="btn btn-danger" onClick={() => handleAction(lr.id, 'manager-reject')}>Reject</button>
                          <button className="btn btn-success" onClick={() => handleAction(lr.id, 'manager-approve')}>Approve</button>
                        </>
                      )}
                      {lr.status === 'MANAGER_APPROVED' && (isHR || isAdmin) && (
                        <>
                          <button className="btn btn-danger" onClick={() => handleAction(lr.id, 'hr-reject')}>Reject</button>
                          <button className="btn btn-success" onClick={() => handleAction(lr.id, 'hr-approve')}>Approve</button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={() => setActionId(lr.id)}>Review & Action</button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* History Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <h3>Approval History</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 13, color: '#6b7280' }}>From:</label>
              <input type="date" className="form-control" style={{ width: 150, padding: '4px 8px', fontSize: 13 }} value={exportFrom} onChange={e => setExportFrom(e.target.value)} />
              <label style={{ fontSize: 13, color: '#6b7280' }}>To:</label>
              <input type="date" className="form-control" style={{ width: 150, padding: '4px 8px', fontSize: 13 }} value={exportTo} onChange={e => setExportTo(e.target.value)} />
              <button className="btn btn-outline" onClick={exportLeaveReport}>📥 Export CSV</button>
            </div>
          </div>
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Manager</th>
                    <th>HR</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLeaves.length === 0 ? (
                    <tr><td colSpan="8" className="empty-state">No history yet</td></tr>
                  ) : historyLeaves.map(lr => (
                    <tr key={lr.id}>
                      <td><strong>{lr.employeeName}</strong><br/><small style={{ color: '#6b7280' }}>{lr.employeeEmpId}</small></td>
                      <td>{lr.leaveTypeName}</td>
                      <td>{lr.startDate} to {lr.endDate}</td>
                      <td>{lr.totalDays}</td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lr.reason}</td>
                      <td><span className={`badge ${statusBadge(lr.status)}`}>{lr.status.replace(/_/g, ' ')}</span></td>
                      <td>{lr.managerName ? <>{lr.managerName}<br/><small>{lr.managerActedAt}</small></> : '—'}</td>
                      <td>{lr.hrName ? <>{lr.hrName}<br/><small>{lr.hrActedAt}</small></> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
